import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Brain, Sparkles, Copy, Check, ExternalLink, TrendingUp, Clock, Target, Zap } from 'lucide-react';
import { conversationFlow, generateWhatsAppURL, generateWhatsAppMessage, bottleneckToSystem, systemDescriptions } from './conversationData';

// Sayfa Farkındalığı - Cold Reading
const getPageAwareness = () => {
  const path = window.location.pathname;
  const referrer = document.referrer;
  
  if (path.includes('aifabric')) {
    return {
      greeting: "Yapay zeka ile iş süreçlerinizi dönüştürmek istiyorsunuz, değil mi?",
      context: "aifabric"
    };
  }
  if (path.includes('portfolyo') || path.includes('proje')) {
    return {
      greeting: "Projelerimizi inceliyorsunuz... Şimdi sizinkini konuşalım mı?",
      context: "portfolio"
    };
  }
  if (path.includes('buyume-sistemleri')) {
    return {
      greeting: "Büyüme sistemlerimizi keşfediyorsunuz. Hangisi sizin için doğru?",
      context: "growth"
    };
  }
  if (path.includes('metodoloji')) {
    return {
      greeting: "20 yıllık metodolojimizi merak ediyorsunuz. Size özel uygulayalım mı?",
      context: "methodology"
    };
  }
  return null;
};

// Saat Farkındalığı
const getTimeAwareness = () => {
  const hour = new Date().getHours();
  
  if (hour >= 23 || hour < 6) {
    return "Gece geç saatte çalışıyorsunuz... Bu iş sizin için gerçekten önemli.";
  }
  if (hour >= 6 && hour < 9) {
    return "Erken kalkan yol alır. Büyüme yolculuğunuza şimdi başlayalım.";
  }
  if (hour >= 18 && hour < 23) {
    return "Mesai sonrası araştırma... Kararlılığınız etkileyici.";
  }
  return null;
};

// İlerleme Çubuğu
const ProgressBar = ({ current, total }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-4 px-4 py-3 bg-zinc-900/80 rounded-xl border border-zinc-800"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-zinc-400 font-medium">TEŞHİS İLERLEMESİ</span>
      <span className="text-xs text-violet-400 font-bold">{current}/{total}</span>
    </div>
    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${(current / total) * 100}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="h-full bg-gradient-to-r from-violet-500 to-pink-500 rounded-full"
      />
    </div>
  </motion.div>
);

// Teşhis Skor Kartı
const DiagnosisScore = ({ userData }) => {
  const urgencyScore = userData.urgency_tag === 'urgent' ? 95 : userData.urgency_tag === 'planned' ? 75 : 50;
  const growthScore = userData.growth_tag === 'aggressive_growth' ? 90 : userData.growth_tag === 'moderate_growth' ? 70 : 55;
  const potentialScore = Math.round((urgencyScore + growthScore) / 2) + Math.floor(Math.random() * 10);
  
  const systemKey = userData.bottleneck_tag;
  const benchmarks = {
    conversion_issue: { avg: 2.1, medyatik: 5.8 },
    perception_issue: { avg: 15, medyatik: 42 },
    performance_issue: { avg: 2.5, medyatik: 8.2 },
    retention_issue: { avg: 25, medyatik: 65 },
    efficiency_issue: { avg: 60, medyatik: 87 }
  };
  
  const benchmark = benchmarks[systemKey] || benchmarks.conversion_issue;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mb-4 p-4 bg-gradient-to-br from-violet-500/10 via-zinc-900 to-pink-500/10 rounded-xl border border-violet-500/30"
    >
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-5 h-5 text-violet-400" />
        <span className="text-sm font-semibold text-white">Büyüme Potansiyel Skoru</span>
      </div>
      
      <div className="flex items-end gap-2 mb-4">
        <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
          {potentialScore}
        </span>
        <span className="text-zinc-400 text-sm mb-1">/100</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-2 bg-zinc-800/50 rounded-lg">
          <div className="text-xs text-zinc-500">Aciliyet</div>
          <div className="text-sm font-semibold text-amber-400">
            {userData.urgency_tag === 'urgent' ? '⚡ YÜKSEK' : userData.urgency_tag === 'planned' ? '📅 ORTA' : '🔍 DÜŞÜK'}
          </div>
        </div>
        <div className="p-2 bg-zinc-800/50 rounded-lg">
          <div className="text-xs text-zinc-500">Tahmini ROI</div>
          <div className="text-sm font-semibold text-emerald-400">+{180 + Math.floor(Math.random() * 60)}%</div>
        </div>
      </div>
      
      {/* Benchmark */}
      <div className="p-3 bg-zinc-800/30 rounded-lg">
        <div className="text-xs text-zinc-500 mb-2">📊 Sektör Karşılaştırması</div>
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-zinc-400">Sektör Ortalaması:</span>
            <span className="text-zinc-300">%{benchmark.avg}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-zinc-400">Medyatik Müşterileri:</span>
            <span className="text-emerald-400 font-semibold">%{benchmark.medyatik}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Mini ROI Hesaplayıcı
const MiniROICalculator = ({ onComplete, userData }) => {
  const [step, setStep] = useState(0);
  const [revenue, setRevenue] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [result, setResult] = useState(null);

  const revenueOptions = [
    { label: "100K - 500K TL", value: 300000 },
    { label: "500K - 1M TL", value: 750000 },
    { label: "1M - 5M TL", value: 2500000 },
    { label: "5M+ TL", value: 7500000 }
  ];

  const conversionOptions = [
    { label: "%1-2 (Düşük)", value: 1.5, potential: 4.5 },
    { label: "%2-5 (Orta)", value: 3.5, potential: 7 },
    { label: "%5+ (İyi)", value: 6, potential: 10 }
  ];

  const handleRevenueSelect = (opt) => {
    setRevenue(opt);
    setStep(1);
  };

  const handleConversionSelect = (opt) => {
    setConversion(opt);
    // Hesapla
    const currentMonthly = revenue.value / 12;
    const potentialIncrease = currentMonthly * ((opt.potential / opt.value) - 1);
    const calcResult = {
      current: currentMonthly,
      potential: potentialIncrease,
      percentage: Math.round(((opt.potential / opt.value) - 1) * 100)
    };
    setResult(calcResult);
    setStep(2);
    
    // 3 saniye sonra devam et
    setTimeout(() => {
      if (onComplete) onComplete(calcResult);
    }, 3000);
  };

  const handleSkip = () => {
    if (onComplete) onComplete(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 bg-zinc-900/80 rounded-xl border border-zinc-800"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          <span className="text-sm font-semibold text-white">Hızlı ROI Tahmini</span>
        </div>
        {step < 2 && (
          <button onClick={handleSkip} className="text-xs text-zinc-500 hover:text-zinc-300">
            Atla →
          </button>
        )}
      </div>

      {step === 0 && (
        <>
          <p className="text-xs text-zinc-400 mb-3">Yaklaşık yıllık geliriniz?</p>
          <div className="grid grid-cols-2 gap-2">
            {revenueOptions.map((opt) => (
              <button
                key={opt.label}
                onClick={() => handleRevenueSelect(opt)}
                className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-violet-500/50 rounded-lg text-xs text-zinc-200 transition-all"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <p className="text-xs text-zinc-400 mb-3">Mevcut dönüşüm oranınız?</p>
          <div className="space-y-2">
            {conversionOptions.map((opt) => (
              <button
                key={opt.label}
                onClick={() => handleConversionSelect(opt)}
                className="w-full px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-violet-500/50 rounded-lg text-xs text-zinc-200 transition-all text-left"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}

      {step === 2 && result && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-lg border border-emerald-500/30"
        >
          <div className="text-center">
            <p className="text-xs text-zinc-400 mb-1">Sistemimizle tahmini aylık ek gelir</p>
            <p className="text-2xl font-bold text-emerald-400">
              +₺{Math.round(result.potential).toLocaleString('tr-TR')}
            </p>
            <p className="text-xs text-zinc-500 mt-1">(%{result.percentage} artış potansiyeli)</p>
            <p className="text-xs text-violet-400 mt-3 animate-pulse">Devam ediliyor...</p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Chat İçi Sosyal Kanıt
const InChatSocialProof = () => {
  const proofs = [
    "🔔 Az önce bir e-ticaret markası teşhis tamamladı",
    "🔔 Bu hafta 14 marka büyüme analizi talep etti",
    "🔔 Son 24 saatte 3 yeni proje başladı"
  ];
  
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (visible) {
      const hideTimer = setTimeout(() => setVisible(false), 4000);
      return () => clearTimeout(hideTimer);
    }
  }, [visible, current]);

  if (!visible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="mb-3 px-3 py-2 bg-violet-500/10 border border-violet-500/20 rounded-lg"
    >
      <p className="text-xs text-violet-300">{proofs[current % proofs.length]}</p>
    </motion.div>
  );
};

// Mesaj Bileşeni
const ChatMessage = ({ text, isBot, isTyping }) => {
  const formatText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}
    >
      {isBot && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center mr-2 flex-shrink-0">
          <Brain className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`max-w-[85%] px-4 py-3 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${
        isBot 
          ? 'bg-zinc-800/80 text-zinc-200 rounded-bl-md' 
          : 'bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-br-md'
      }`}>
        {isTyping ? (
          <div className="flex gap-1 py-1">
            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        ) : (
          formatText(text)
        )}
      </div>
    </motion.div>
  );
};

// Option Butonları
const OptionButtons = ({ options, onSelect, disabled }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="flex flex-col gap-2 mb-4"
  >
    {options.map((option) => (
      <button
        key={option.id}
        onClick={() => !disabled && onSelect(option)}
        disabled={disabled}
        className="w-full text-left px-4 py-3 bg-zinc-800/60 hover:bg-zinc-700/80 border border-zinc-700 hover:border-violet-500/50 rounded-xl text-sm text-zinc-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {option.label}
      </button>
    ))}
  </motion.div>
);

// Form Bileşeni
const LeadForm = ({ fields, submitLabel, onSubmit, disabled }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors(prev => ({ ...prev, [id]: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    
    fields.forEach(field => {
      if (field.required && !formData[field.id]?.trim()) {
        newErrors[field.id] = 'Bu alan zorunludur';
      }
      if (field.type === 'email' && formData[field.id] && !/\S+@\S+\.\S+/.test(formData[field.id])) {
        newErrors[field.id] = 'Geçerli bir e-posta girin';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onSubmit={handleSubmit}
      className="space-y-3 mb-4"
    >
      {fields.map((field) => (
        <div key={field.id}>
          <label className="block text-xs text-zinc-400 mb-1">{field.label}</label>
          {field.type === 'select' ? (
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-violet-500 transition-colors"
            >
              <option value="">Seçiniz...</option>
              {field.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : field.type === 'textarea' ? (
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors resize-none"
            />
          ) : (
            <input
              type={field.type}
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors"
            />
          )}
          {errors[field.id] && (
            <p className="text-xs text-red-400 mt-1">{errors[field.id]}</p>
          )}
        </div>
      ))}
      <button
        type="submit"
        disabled={disabled}
        className="w-full py-3 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {submitLabel}
      </button>
    </motion.form>
  );
};

// WhatsApp Butonları
const WhatsAppActions = ({ userData, onComplete }) => {
  const [copied, setCopied] = useState(false);
  const whatsappUrl = generateWhatsAppURL(userData);
  const message = generateWhatsAppMessage(userData);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleWhatsAppClick = () => {
    window.open(whatsappUrl, '_blank');
    setTimeout(() => onComplete(), 1000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-3 mb-4"
    >
      <button
        onClick={handleWhatsAppClick}
        className="w-full py-3.5 bg-[#25D366] hover:bg-[#20BD5A] text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        WhatsApp Analizini Gönder
        <ExternalLink className="w-4 h-4" />
      </button>
      
      <button
        onClick={handleCopy}
        className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-medium rounded-xl flex items-center justify-center gap-2 transition-colors"
      >
        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        {copied ? 'Kopyalandı!' : 'Mesajı Kopyala'}
      </button>
    </motion.div>
  );
};

// Exit Intent Modal
const ExitIntentModal = ({ onStay, onLeave }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="absolute inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 z-10"
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="bg-zinc-900 border border-violet-500/30 rounded-2xl p-6 text-center"
    >
      <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
        <Zap className="w-6 h-6 text-amber-400" />
      </div>
      <h3 className="text-lg font-bold text-white mb-2">Durun!</h3>
      <p className="text-sm text-zinc-400 mb-4">
        Ücretsiz büyüme teşhisinizi tamamlamadan gitmeyin.<br/>
        Sadece 30 saniye sürecek.
      </p>
      <div className="space-y-2">
        <button
          onClick={onStay}
          className="w-full py-2.5 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-medium rounded-xl"
        >
          ✅ Evet, 30 saniyemi ayırabilirim
        </button>
        <button
          onClick={onLeave}
          className="w-full py-2.5 bg-zinc-800 text-zinc-400 rounded-xl text-sm"
        >
          Başka zaman
        </button>
      </div>
    </motion.div>
  </motion.div>
);

// Ana Chatbot Bileşeni
export function ZihinMimariChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentStage, setCurrentStage] = useState('stage_0_entry');
  const [userData, setUserData] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [showROICalculator, setShowROICalculator] = useState(false);
  const [showDiagnosisScore, setShowDiagnosisScore] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [diagnosisStep, setDiagnosisStep] = useState(0);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [hasTriggeredExit, setHasTriggeredExit] = useState(false);
  const [hasShownTrigger, setHasShownTrigger] = useState(false);
  const messagesEndRef = useRef(null);
  const processingRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, showOptions, showForm, showWhatsApp, showROICalculator, showDiagnosisScore, scrollToBottom]);

  // Time on page trigger messages - Rotating
  const triggerMessages = [
    {
      text: "Hâlâ buradasınız.\nDemek ki büyümeyi ciddiye alıyorsunuz.\n\nİsterseniz 3 dakikalık bir testle nerede kazandığınızı, nerede kaybettiğinizi gösterelim.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "3 dakikada markanızın büyüme potansiyelini ölçebilirim.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "Bu hafta 14 marka benimle büyüme analizi yaptı.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "Her geçen gün rakipleriniz güçleniyor.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "Ücretsiz teşhis ile kaçırdığınız fırsatları göstermeme izin verin.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "Web siteniz ziyaretçileri müşteriye dönüştürüyor mu?",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "30 saniye ayırın, 6 aylık büyüme planınızı çıkaralım.",
      cta: "Büyümeyi Test Et"
    },
    {
      text: "Dönüşüm oranınızı %240 artıran metodolojimizi merak ediyor musunuz?",
      cta: "Büyümeyi Test Et"
    }
  ];

  const [triggerMessageIndex, setTriggerMessageIndex] = useState(0);

  // Time on page trigger
  useEffect(() => {
    if (!hasShownTrigger && !isOpen) {
      // Random message seç
      setTriggerMessageIndex(Math.floor(Math.random() * triggerMessages.length));
      
      const timer = setTimeout(() => {
        setHasShownTrigger(true);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [hasShownTrigger, isOpen]);

  const processStage = useCallback(async (stageName, additionalData = {}) => {
    if (processingRef.current) return;
    processingRef.current = true;
    
    const stage = conversationFlow[stageName];
    if (!stage) {
      processingRef.current = false;
      return;
    }

    setShowOptions(false);
    setShowForm(false);
    setShowWhatsApp(false);
    setShowROICalculator(false);
    setShowDiagnosisScore(false);

    // Focus mode kontrolü
    if (stage.enableFocusMode) setFocusMode(true);
    if (stage.disableFocusMode) setFocusMode(false);

    // Diagnosis step tracking
    if (stageName === 'stage_1_question_1') setDiagnosisStep(1);
    if (stageName === 'stage_1_question_2') setDiagnosisStep(2);
    if (stageName === 'stage_1_question_3') setDiagnosisStep(3);

    // Mesajları al (dinamik veya statik)
    let stageMessages = stage.messages || [];
    if (stage.dynamicMessages) {
      stageMessages = stage.dynamicMessages({ ...userData, ...additionalData });
    }

    // Mesajları sırayla göster
    for (const msg of stageMessages) {
      setIsTyping(true);
      await new Promise(resolve => setTimeout(resolve, msg.delay || 1000));
      setIsTyping(false);
      setMessages(prev => [...prev, { text: msg.text, isBot: true }]);
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // Soru varsa göster
    if (stage.question) {
      await new Promise(resolve => setTimeout(resolve, 500));
      setMessages(prev => [...prev, { text: stage.question, isBot: true, isQuestion: true }]);
      if (stage.subtitle) {
        await new Promise(resolve => setTimeout(resolve, 300));
        setMessages(prev => [...prev, { text: stage.subtitle, isBot: true, isSubtitle: true }]);
      }
    }

    // Sonraki adımı belirle
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (stage.autoNext) {
      processingRef.current = false;
      setCurrentStage(stage.autoNext); // Stage'i güncelle
      processStage(stage.autoNext, additionalData);
    } else if (stage.options) {
      setShowOptions(true);
      processingRef.current = false;
      // Options görünür olduğunda scroll
      setTimeout(() => scrollToBottom(), 200);
    } else if (stage.form) {
      setShowForm(true);
      processingRef.current = false;
      setTimeout(() => scrollToBottom(), 200);
    } else if (stage.showWhatsAppButton) {
      setShowWhatsApp(true);
      processingRef.current = false;
    } else if (stage.showROICalculator) {
      setShowROICalculator(true);
      processingRef.current = false;
      setTimeout(() => scrollToBottom(), 200);
    } else if (stage.showDiagnosisScore) {
      setShowDiagnosisScore(true);
      processingRef.current = false;
      setTimeout(() => scrollToBottom(), 200);
      // Score gösterildikten sonra devam
      if (stage.nextAfterScore) {
        setTimeout(() => {
          setShowDiagnosisScore(false);
          setCurrentStage(stage.nextAfterScore);
          processStage(stage.nextAfterScore, additionalData);
        }, stage.autoNextDelay || 3000);
      }
    } else {
      processingRef.current = false;
    }
  }, [userData, scrollToBottom]);

  const handleOpen = () => {
    setIsOpen(true);
    setHasShownTrigger(false);
    
    if (messages.length === 0) {
      // Cold reading - sayfa ve saat farkındalığı
      const pageAwareness = getPageAwareness();
      const timeAwareness = getTimeAwareness();
      
      let customIntro = [];
      if (pageAwareness) {
        customIntro.push({ text: pageAwareness.greeting, isBot: true });
      }
      if (timeAwareness && !pageAwareness) {
        customIntro.push({ text: timeAwareness, isBot: true });
      }
      
      if (customIntro.length > 0) {
        setMessages(customIntro);
        setTimeout(() => processStage('stage_0_entry'), 1500);
      } else {
        processStage('stage_0_entry');
      }
    }
  };

  const handleClose = () => {
    // Exit intent - sadece teşhis sırasında
    if (diagnosisStep > 0 && diagnosisStep < 3 && !hasTriggeredExit) {
      setShowExitIntent(true);
      setHasTriggeredExit(true);
      return;
    }
    
    setIsOpen(false);
    setFocusMode(false);
  };

  const handleExitStay = () => {
    setShowExitIntent(false);
  };

  const handleExitLeave = () => {
    setShowExitIntent(false);
    setIsOpen(false);
    setFocusMode(false);
  };

  const handleOptionSelect = (option) => {
    // Kullanıcı mesajını ekle
    setMessages(prev => [...prev, { text: option.label, isBot: false }]);
    
    // Veriyi kaydet
    const newData = { ...userData };
    if (option.value) {
      if (currentStage === 'stage_1_question_1') {
        newData.bottleneck = option.value;
        newData.bottleneck_tag = option.tag;
      } else if (currentStage === 'stage_1_question_2') {
        newData.growth_target = option.value;
        newData.growth_tag = option.tag;
      } else if (currentStage === 'stage_1_question_3') {
        newData.urgency = option.value;
        newData.urgency_tag = option.tag;
      }
    }
    setUserData(newData);

    // Özel aksiyonlar
    if (option.action === 'restart') {
      setMessages([]);
      setUserData({});
      setCurrentStage('stage_0_entry');
      setDiagnosisStep(0);
      processingRef.current = false;
      processStage('stage_0_entry');
      return;
    }
    if (option.action === 'close') {
      setIsOpen(false);
      setFocusMode(false);
      return;
    }

    // Sonraki aşamaya geç
    if (option.next) {
      setCurrentStage(option.next);
      setTimeout(() => processStage(option.next, newData), 500);
    }
  };

  const handleFormSubmit = (formData) => {
    const stage = conversationFlow[currentStage];
    const newData = { ...userData, ...formData };
    setUserData(newData);
    
    if (stage.form?.next) {
      setCurrentStage(stage.form.next);
      setTimeout(() => processStage(stage.form.next, newData), 500);
    }
  };

  const handleWhatsAppComplete = () => {
    setCurrentStage('stage_6_confirmation');
    processStage('stage_6_confirmation');
  };

  const handleROIComplete = (roiResult) => {
    setShowROICalculator(false);
    // ROI tamamlandığında Lead Transition'a geç
    setCurrentStage('stage_2c_lead_transition');
    setTimeout(() => processStage('stage_2c_lead_transition', userData), 500);
  };

  const currentStageData = conversationFlow[currentStage];

  return (
    <>
      {/* Focus Mode Overlay */}
      <AnimatePresence>
        {focusMode && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 bg-black/85 backdrop-blur-sm z-40"
          />
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: isOpen ? 0 : 1 }}
        transition={{ delay: 0.5, type: 'spring' }}
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 w-[72px] h-[72px] rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 text-white shadow-2xl hover:shadow-violet-500/40 transition-all duration-300 flex items-center justify-center group"
        data-testid="zihin-mimari-button"
      >
        <Brain className="w-8 h-8 group-hover:scale-110 transition-transform" />
        <span className="absolute inset-0 rounded-full bg-violet-500 animate-ping opacity-20" />
        <span className="absolute -inset-1 rounded-full border-2 border-violet-400/30 animate-pulse" />
      </motion.button>

      {/* Time on page trigger badge */}
      <AnimatePresence>
        {hasShownTrigger && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed bottom-24 right-6 z-50 max-w-[300px] p-4 bg-zinc-900/95 backdrop-blur-sm border border-violet-500/30 rounded-2xl shadow-2xl cursor-pointer hover:border-violet-500/50 transition-colors"
            onClick={handleOpen}
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  {triggerMessages[triggerMessageIndex]?.text}{" "}
                  <span className="text-violet-400 font-medium">
                    {triggerMessages[triggerMessageIndex]?.cta}
                  </span>
                </p>
              </div>
            </div>
            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setHasShownTrigger(false);
              }}
              className="absolute top-2 right-2 w-5 h-5 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[700px] max-h-[calc(100vh-48px)] bg-[#0A0A0A] border border-zinc-800 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="zihin-mimari-window"
          >
            {/* Exit Intent Modal */}
            <AnimatePresence>
              {showExitIntent && (
                <ExitIntentModal onStay={handleExitStay} onLeave={handleExitLeave} />
              )}
            </AnimatePresence>

            {/* Header */}
            <div className="p-4 bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-pink-500/10 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Zihin Mimarı</h3>
                  <p className="text-emerald-400 text-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Taner Torun AI Asistanı
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Progress Bar - Teşhis sırasında */}
              {diagnosisStep > 0 && diagnosisStep <= 3 && (
                <ProgressBar current={diagnosisStep} total={3} />
              )}

              {/* In-Chat Social Proof */}
              <InChatSocialProof />

              {messages.map((msg, i) => (
                <ChatMessage key={i} text={msg.text} isBot={msg.isBot} />
              ))}

              {/* Typing Indicator */}
              {isTyping && <ChatMessage isBot isTyping />}

              {/* Options */}
              {showOptions && conversationFlow[currentStage]?.options && (
                <OptionButtons 
                  options={conversationFlow[currentStage].options} 
                  onSelect={handleOptionSelect}
                  disabled={isTyping}
                />
              )}

              {/* Diagnosis Score */}
              {showDiagnosisScore && userData.bottleneck_tag && (
                <DiagnosisScore userData={userData} />
              )}

              {/* Mini ROI Calculator */}
              {showROICalculator && (
                <MiniROICalculator onComplete={handleROIComplete} userData={userData} />
              )}

              {/* Form */}
              {showForm && conversationFlow[currentStage]?.form && (
                <LeadForm
                  fields={conversationFlow[currentStage].form.fields}
                  submitLabel={conversationFlow[currentStage].form.submitLabel}
                  onSubmit={handleFormSubmit}
                  disabled={isTyping}
                />
              )}

              {/* WhatsApp Actions */}
              {showWhatsApp && (
                <WhatsAppActions 
                  userData={userData} 
                  onComplete={handleWhatsAppComplete}
                />
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-zinc-800 flex-shrink-0">
              <p className="text-center text-xs text-zinc-500">
                🧠 20 yıllık Taner Torun Metodolojisi ile güçlendirilmiştir
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ZihinMimariChat;
